/**
 * TrackerRegistry — Dynamic WSS tracker list for WebTorrent browser engine.
 *
 * Tracker source: https://github.com/ngosang/trackerslist
 *   Maintained by @ngosang, updated daily via GitHub Actions.
 *
 * Fetch priority (industry standard CDN chain):
 *   1. jsDelivr GitHub CDN   — global edge CDN, 99.99% SLA, automatic cache purge on commit
 *      https://cdn.jsdelivr.net/gh/ngosang/trackerslist@master/trackers_all_ws.txt
 *
 *   2. raw.githubusercontent.com — direct GitHub source, bypasses jsDelivr if down
 *      https://raw.githubusercontent.com/ngosang/trackerslist/master/trackers_all_ws.txt
 *
 *   3. Hardcoded fallback        — if both CDN sources are unavailable (offline / CORS)
 *
 * Cache: 24 h in-memory. Warm-up triggered by WebTorrentEngine.configure().
 * Filter: wss:// only — browsers require TLS WebSocket for BitTorrent tracker protocol.
 */

import { TrackerConfig } from '../../../../shared/config/tracker-config';

/** Hardcoded fallback — last-resort when all CDN sources fail */
export const FALLBACK_WSS_TRACKERS: string[] = [
  ...TrackerConfig.defaultWssTrackers,
];

/**
 * CDN source chain — tried in order, first success wins.
 *
 * jsDelivr GitHub CDN is preferred:
 *   - Global PoP (280+ cities), auto-purged on GitHub push, HTTPS only.
 *   - Pattern: https://cdn.jsdelivr.net/gh/{owner}/{repo}@{ref}/{path}
 */
const TRACKER_SOURCES: string[] = [
  // 1. jsDelivr GitHub CDN (primary — fastest, most reliable)
  'https://cdn.jsdelivr.net/gh/ngosang/trackerslist@master/trackers_all_ws.txt',
  // 2. GitHub raw (secondary — authoritative source)
  'https://raw.githubusercontent.com/ngosang/trackerslist/master/trackers_all_ws.txt',
];

/** Cache TTL: 24 hours */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** Request timeout per source: 8 seconds */
const FETCH_TIMEOUT_MS = 8_000;

interface TrackerCache {
  trackers:  string[];
  fetchedAt: number;
  source:    string;
}

let _cache: TrackerCache | null = null;
let _fetchPromise: Promise<string[]> | null = null;

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Get the current WSS tracker list synchronously.
 * Returns cache if fresh. Triggers async background refresh on cache miss.
 * Never throws — always returns at least FALLBACK_WSS_TRACKERS.
 */
export function getWssTrackers(): string[] {
  const now = Date.now();

  if (_cache && now - _cache.fetchedAt < CACHE_TTL_MS) {
    return _cache.trackers;
  }

  // Trigger background refresh (fire-and-forget)
  if (!_fetchPromise) {
    _fetchPromise = _fetchFromChain()
      .then(({ trackers, source }) => {
        _cache = { trackers, fetchedAt: Date.now(), source };
        _fetchPromise = null;
        console.info(`[TrackerRegistry] ${trackers.length} WSS trackers loaded via ${source}`);
        return trackers;
      })
      .catch((err) => {
        console.warn('[TrackerRegistry] all sources failed, using fallback:', err);
        _fetchPromise = null;
        if (!_cache) {
          _cache = { trackers: FALLBACK_WSS_TRACKERS, fetchedAt: Date.now(), source: 'fallback' };
        }
        return _cache.trackers;
      });
  }

  return _cache?.trackers ?? FALLBACK_WSS_TRACKERS;
}

/**
 * Force-refresh the tracker list immediately.
 * Awaitable — useful to call at engine startup or before first download.
 * Returns the refreshed list.
 */
export async function refreshTrackers(): Promise<string[]> {
  try {
    const { trackers, source } = await _fetchFromChain();
    _cache = { trackers, fetchedAt: Date.now(), source };
    console.info(`[TrackerRegistry] refreshed ${trackers.length} WSS trackers via ${source}`);
    return trackers;
  } catch (err) {
    console.warn('[TrackerRegistry] refresh failed, using fallback:', err);
    return _cache?.trackers ?? FALLBACK_WSS_TRACKERS;
  }
}

/**
 * Merge WSS trackers from a magnet URI with the live registry list.
 * Deduplicates and filters to wss:// only.
 */
export function mergeWssTrackers(magnetUri?: string): string[] {
  const base = getWssTrackers();
  if (!magnetUri) return base;

  const extra: string[] = [];
  try {
    const url = new URL(magnetUri);
    url.searchParams.getAll('tr').forEach((tr) => {
      const decoded = decodeURIComponent(tr);
      if (decoded.startsWith('wss://') && !base.includes(decoded)) {
        extra.push(decoded);
      }
    });
  } catch {
    // malformed magnet — skip
  }

  return [...base, ...extra];
}

// ─── Internal ─────────────────────────────────────────────────────────────────

/** Try each source URL in order. Returns first successful result. */
async function _fetchFromChain(): Promise<{ trackers: string[]; source: string }> {
  const errors: string[] = [];

  for (const url of TRACKER_SOURCES) {
    try {
      const trackers = await _fetchTrackerList(url);
      return { trackers, source: url };
    } catch (err) {
      errors.push(`${url}: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  throw new Error(`All tracker sources failed:\n${errors.join('\n')}`);
}

/** Fetch and parse a tracker list from a single URL */
async function _fetchTrackerList(url: string): Promise<string[]> {
  const res = await fetch(url, {
    cache: 'no-store',
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`);
  }

  const text = await res.text();

  // Format: one URL per line, blank lines as separators
  const trackers = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('wss://'));

  if (trackers.length === 0) {
    throw new Error('Parsed 0 wss:// trackers from response');
  }

  return trackers;
}
